import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "@/services/api";

export const getDeliveries = createAsyncThunk(
    "delivery/getDeliveries",
    async (data, { rejectWithValue }) => {
        try {
            const page = data?.page ?? 1;
            const type = data?.type ?? "assigned";
            let url = `delivery-partner/get-assigned-deliveries?type=${encodeURIComponent(type)}&page=${page}`;
            if (data?.start_date) {
                url += `&start_date=${encodeURIComponent(data.start_date)}`;
            }
            if (data?.end_date) {
                url += `&end_date=${encodeURIComponent(data.end_date)}`;
            }
            const response = await api.get(url);
            return response.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || "Failed to get deliveries.");
        }
    }
);

export const getOrderVendor = createAsyncThunk(
    "delivery/getOrderVendor",
    async (data, { rejectWithValue }) => {
        try {
            const response = await api.post(`delivery-partner/get-order-vendors`, {
                order_id: data?.order_id
            });
            return response.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || "Failed to get vendors.");
        }
    }
);

export const acceptRejectOrder = createAsyncThunk(
    "delivery/acceptRejectOrder",
    async (data, { rejectWithValue }) => {
        try {
            const response = await api.post(`delivery-partner/approve-reject-deliveries`, data);
            return response.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || "Failed to accept/decline order.");
        }
    }
);

export const updateDeliveryProfile = createAsyncThunk(
    "delivery/updateDeliveryProfile",
    async (data, { rejectWithValue }) => {
        try {
            const formData = new FormData();
            Object.keys(data).forEach((key) => {
                if (
                    key !== "residence_image" &&
                    key !== "driving_licence_image" &&
                    key !== "insurance_image"
                ) {
                    formData.append(key, data[key] ?? "");
                }
            });
            if (data.residence_image) {
                formData.append("residence_image", data.residence_image);
            }
            if (data.driving_licence_image) {
                formData.append("driving_licence_image", data.driving_licence_image);
            }
            if (data.insurance_image) {
                formData.append("insurance_image", data.insurance_image);
            }
            const response = await api.post(
                `delivery-partner/update-delivery-data`,
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                }
            );
            return response.data;
        } catch (err) {
            return rejectWithValue(
                err.response?.data || "Failed to update profile."
            );
        }
    }

);

export const getVendorProducts = createAsyncThunk(
    "delivery/getVendorProducts",
    async ({ vendor_id, order_id }, { rejectWithValue }) => {
        try {
            const response = await api.get(
                `delivery-partner/get-delivery-products?vendor_id=${vendor_id}&order_id=${order_id}`
            );
            return response.data;
        } catch (err) {
            return rejectWithValue(
                err.response?.data || "Failed to get vendor products."
            );
        }
    }
);

export const getDropOffProducts = createAsyncThunk(
    "delivery/getDropOffProducts",
    async ({ id }, { rejectWithValue }) => {
        try {
            const response = await api.post(`delivery-partner/get-drop-off-products`, { id });
            return response.data;
        } catch (err) {
            return rejectWithValue(
                err.response?.data || "Failed to get drop off products."
            );
        }
    }
);

export const getUploadedProof = createAsyncThunk(
    "delivery/getUploadedProof",
    async ({ id }, { rejectWithValue }) => {
        try {
            const response = await api.get(`delivery-partner/fetch-delivery-proofs?delivery_assigned_order_id=${id}`);
            return response.data;
        } catch (err) {
            return rejectWithValue(
                err.response?.data || "Failed to get uploaded proof."
            );
        }
    }
);

export const getUploadedPickupProof = createAsyncThunk(
    "delivery/getUploadedPickupProof",
    async ({ id }, { rejectWithValue }) => {
        try {
            const response = await api.get(`delivery-partner/fetch-pickup-proofs?delivery_assigned_order_id=${id}`);
            return response.data;
        } catch (err) {
            return rejectWithValue(
                err.response?.data || "Failed to get uploaded pickup proof."
            );
        }
    }
);


export const deleteUploadedProof = createAsyncThunk(
    "delivery/deleteUploadedProof",
    async ({ id }, { rejectWithValue }) => {
        try {
            const response = await api.post(`delivery-partner/delete-delivery-proof`, { id });
            return response.data;
        } catch (err) {
            return rejectWithValue(
                err.response?.data || "Failed to delete uploaded proof."
            );
        }
    }
);

export const getCompletedOrderDetails = createAsyncThunk(
    "delivery/getCompletedOrderDetails",
    async ({ id }, { rejectWithValue }) => {
        try {
            const response = await api.get(`delivery-partner/get-completed-orders-summary?order_id=${id}`);
            return response.data;
        } catch (err) {
            return rejectWithValue(
                err.response?.data || "Failed to get completed order summary."
            );
        }
    }
);

export const getCompletedOrderProducts = createAsyncThunk(
    "delivery/getCompletedOrderProducts",
    async ({ id }, { rejectWithValue }) => {
        try {
            const response = await api.get(`delivery-partner/get-completed-products?order_id=${id}`);
            return response.data;
        } catch (err) {
            return rejectWithValue(
                err.response?.data || "Failed to get completed order products."
            );
        }
    }
);

export const updatePickedQuantity = createAsyncThunk(
    "delivery/updatePickedQuantity",
    async (data, { rejectWithValue }) => {
        try {
            const formData = new FormData();
            data.forEach((item) => {
                formData.append("delivery_assigned_orders_id[]", item.id);
                formData.append("picked_quantity[]", item.collected_quantity);
            });

            const response = await api.post(
                "delivery-partner/update-picked-quantity",
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                }
            );
            if (response.data.status === false) {
                return rejectWithValue(response.data);
            }
            return response.data;
        } catch (err) {
            return rejectWithValue(
                err.response?.data || "Failed to update picked quantity."
            );
        }
    }
);

export const downloadDeliveryPDF = createAsyncThunk(
    "delivery/downloadDeliveryPDF",
    async (data, { rejectWithValue }) => {
        try {
            const formData = new FormData();
            data.forEach((id) => {
                formData.append("delivery_assigned_order_id[]", id);
            });
            const response = await api.post(
                "delivery-partner/download-delivery-product",
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                }
            );
            return response.data;
        } catch (err) {
            return rejectWithValue(
                err.response?.data || "Failed to generate PDF."
            );
        }
    }
);

export const startDropOff = createAsyncThunk("delivery/startDropOff",
    async (data, { rejectWithValue }) => {
        try {
            const formData = new FormData();
            data.forEach((id) => {
                formData.append("delivery_assigned_order_id[]", id);
            });
            const response = await api.post(
                "delivery-partner/start-drop-off",
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                }
            );
            if (response.data.status === false) {
                return rejectWithValue(response.data);
            }
            return response.data;
        } catch (err) {
            return rejectWithValue(
                err.response?.data || "Failed to start drop-off."
            );
        }
    }
);

export const uploadDeliveryProof = createAsyncThunk(
    "delivery/uploadDeliveryProof",
    async (data, { rejectWithValue }) => {
        try {
            const formData = new FormData();
            formData.append("delivery_assigned_orders_id", data.id);
            formData.append("remark", data.remark || "");
            formData.append("status", data.status);

            if (data.images && data.images.length > 0) {
                data.images.forEach((img) => {
                    if (img?.file instanceof File) {
                        formData.append("images[]", img.file);
                    }
                });
            }
            if (data.receipts && data.receipts.length > 0) {
                data.receipts.forEach((receipt) => {
                    if (receipt?.file instanceof File) {
                        formData.append("receipts[]", receipt.file);
                    }
                });
            }

            const response = await api.post(
                "delivery-partner/upload-pickup-proof",
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                }
            );
            if (response.data.status === false) {
                return rejectWithValue(response.data);
            }
            return response;
        } catch (err) {
            return rejectWithValue(
                err.response?.data || "Failed to upload pickup proof."
            );
        }
    }
);

export const uploadActualDeliveryProof = createAsyncThunk(
    "delivery/uploadActualDeliveryProof",
    async (data, { rejectWithValue }) => {
        try {
            const formData = new FormData();
            formData.append("delivery_assigned_orders_id", data.id);
            formData.append("remark", data.remark || "");

            if (data.images && data.images.length > 0) {
                data.images.forEach((img) => {
                    if (img?.file instanceof File) {
                        formData.append("images[]", img.file);
                    }
                });
            }

            if (data.receipts && data.receipts.length > 0) {
                data.receipts.forEach((receipt) => {
                    if (receipt?.file instanceof File) {
                        formData.append("receipts[]", receipt.file);
                    }
                });
            }

            const response = await api.post(
                "delivery-partner/upload-delivery-proof",
                formData,
                { headers: { "Content-Type": "multipart/form-data" } }
            );

            // response khud body hai — response.data nahi
            if (response?.status === false) {
                return rejectWithValue(response);
            }
            return response;
        } catch (err) {
            return rejectWithValue(
                err.response?.data || { message: "Failed to upload delivery proof." }
            );
        }
    }
);

export const newOrderDetails = createAsyncThunk(
    "delivery/newOrderDetails",
    async (data, { rejectWithValue }) => {
        try {
            const response = await api.get(`delivery-partner/new-order-details/${data?.order_id}`);
            return response.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || "Failed to get order details.");
        }
    }
);

export const updateDeliveryDP = createAsyncThunk(
    "delivery/updateDeliveryDP",
    async (file, { rejectWithValue }) => {
        try {
            const formData = new FormData();
            formData.append("profile_image", file);
            const response = await api.post(
                `delivery-partner/update-delivery-partner-dp`,
                formData,
                {
                    headers: { "Content-Type": "multipart/form-data" },
                }
            );
            return response.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || "Failed to update DP.");
        }
    }
);

export const requestAccountDeletion = createAsyncThunk(
    "user/requestAccountDeletion",
    async (_, { rejectWithValue }) => {
        try {
            const response = await api.post("delivery-partner/account-deletion-request");
            return response;
        } catch (err) {
            return rejectWithValue(
                err.response?.data || "Failed to request account deletion."
            );
        }
    }
);

export const getLoginUserData = createAsyncThunk(
    "delivery/getLoginUserData",
    async (_, { rejectWithValue }) => {
        try {
            const response = await api.get(`delivery-partner/get-login-user-data`);
            return response?.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || "Failed to get profile data.");
        }
    }
);

export const getEarningStatement = createAsyncThunk(
    "delivery/getEarningStatement",
    async (_, { rejectWithValue }) => {
        try {
            const response = await api.get(`delivery-partner/get-earning-statement`);
            return response.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || "Failed to fetch earnings.");
        }
    }
);

export const rejectAssignedDeliveries = createAsyncThunk(
    "delivery/rejectAssignedDeliveries",
    async ({ delivery_assigned_order_ids, description }, { rejectWithValue }) => {
        try {
            const formData = new FormData();
            delivery_assigned_order_ids.forEach((id) => {
                formData.append("delivery_assigned_order_ids[]", id);
            });
            formData.append("description", description);

            const response = await api.post(
                "delivery-partner/reject-assigned-delivery",
                formData,
                {
                    headers: { "Content-Type": "multipart/form-data" },
                }
            );
            if (response.data.status === false) {
                return rejectWithValue(response.data);
            }
            return response.data;
        } catch (err) {
            return rejectWithValue(
                err.response?.data || "Failed to reject delivery assignments."
            );
        }
    }
);

export const reviewDeliveryProof = createAsyncThunk(
    'delivery/reviewDeliveryProof',
    async ({ delivery_assigned_orders_id, admin_status, rejected_reason }, { rejectWithValue }) => {
        try {
            const response = await api.post('admin/update-delivery-proof-status', {
                delivery_assigned_orders_id,
                admin_status,
                rejected_reason,
            });

            return response;
        } catch (err) {
            return rejectWithValue(
                err.response?.data || 'Failed to review delivery proof.'
            );
        }
    }
);

export const getRejectedDeliveryAssignments = createAsyncThunk(
    'delivery/getRejectedDeliveryAssignments',
    async (_, thunkAPI) => {
        try {
            const response = await api.get(
                'delivery-partner/get-rejected-delivery-assignments'
            );

            const payload = response.data;
            const list = Array.isArray(payload) ? payload : payload?.data || [];

            return list;
        } catch (error) {
            return thunkAPI.rejectWithValue(
                error.response?.data || error.message
            );
        }
    }
);

export const reUploadDeliveryProof = createAsyncThunk(
    'delivery/reUploadDeliveryProof',
    async (payload, { rejectWithValue }) => {
        try {
            const formData = new FormData();

            formData.append(
                'delivery_assigned_orders_id',
                payload.delivery_assigned_orders_id
            );

            formData.append('remark', payload.remark);

            payload.images.forEach((img) => {
                formData.append('images[]', img.file);
            });

            payload.receipts.forEach((rec) => {
                formData.append('receipts[]', rec.file);
            });

            const response = await api.post(
                'delivery-partner/re-upload-delivery-proof',
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );

            const body = response.data;

            if (Array.isArray(body)) {
                return {
                    status: true,
                    message: 'Delivery proof re-uploaded successfully. Awaiting admin approval.',
                    data: body,
                };
            }

            return body;
        } catch (error) {
            return rejectWithValue(
                error.response?.data || { message: 'Something went wrong' }
            );
        }
    }
);


const deliverySlice = createSlice({
    name: "delivery",
    initialState: {
        deliveries: [],
        order_vendors: [],
        vendor_products: [],
        new_order_details: null,
        loading: false,
        vendor_products_loading: false,
        list_loading: false,
        hasMore: true,
        drop_off_produts: [],
        uploaded_proofes: [],
        completed_order_details: {},
        profile_data: null,
        earningStatement: null,
        earningLoading: false,
        rejected_assignments: [],
        rejected_loading: false,
        reupload_loading: false,
    },
    reducers: {
        resetStatus(state) {
            state.loading = false;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getDeliveries.pending, (state) => {
                state.list_loading = true;
            })
            .addCase(getDeliveries.fulfilled, (state, action) => {
                state.list_loading = false;
                state.deliveries = action?.payload;
            })
            .addCase(getDeliveries.rejected, (state) => {
                state.list_loading = false;
            })
            .addCase(getOrderVendor.pending, (state) => {
                state.list_loading = true;
            })
            .addCase(getOrderVendor.fulfilled, (state, action) => {
                state.list_loading = false;
                state.order_vendors = action?.payload;
            })
            .addCase(getOrderVendor.rejected, (state) => {
                state.list_loading = false;
            })
            .addCase(acceptRejectOrder.pending, (state) => {
                state.loading = true;
            })
            .addCase(acceptRejectOrder.fulfilled, (state, action) => {
                state.loading = false;
            })
            .addCase(acceptRejectOrder.rejected, (state) => {
                state.loading = false;
            })
            .addCase(updateDeliveryProfile.pending, (state) => {
                state.loading = true;
            })
            .addCase(updateDeliveryProfile.fulfilled, (state, action) => {
                state.loading = false;
            })
            .addCase(updateDeliveryProfile.rejected, (state) => {
                state.loading = false;
            })
            .addCase(getVendorProducts.pending, (state) => {
                state.vendor_products_loading = true;
            })
            .addCase(getVendorProducts.fulfilled, (state, action) => {
                state.vendor_products_loading = false;

                state.vendor_products = Array.isArray(action.payload)
                    ? action.payload
                    : action.payload?.data || [];
            })
            .addCase(getVendorProducts.rejected, (state) => {
                state.vendor_products_loading = false;
            })
            .addCase(getDropOffProducts.pending, (state) => {
                state.loading = true;
            })
            .addCase(getDropOffProducts.fulfilled, (state, action) => {
                state.loading = false;
                state.drop_off_produts = action.payload;
            })
            .addCase(getDropOffProducts.rejected, (state) => {
                state.loading = false;
            })
            .addCase(getUploadedProof.pending, (state) => {
                state.loading = true;
            })
            .addCase(getUploadedProof.fulfilled, (state, action) => {
                state.loading = false;
                state.uploaded_proofes = action.payload;
            })
            .addCase(getUploadedProof.rejected, (state) => {
                state.loading = false;
            })
            .addCase(getUploadedPickupProof.pending, (state) => {
                state.loading = true;
            })
            .addCase(getUploadedPickupProof.fulfilled, (state, action) => {
                state.loading = false;
                state.uploaded_pickup_proofes = action.payload;
            })
            .addCase(getUploadedPickupProof.rejected, (state) => {
                state.loading = false;
            })
            .addCase(deleteUploadedProof.pending, (state) => {
                state.loading = true;
            })
            .addCase(deleteUploadedProof.fulfilled, (state, action) => {
                state.loading = false;
            })
            .addCase(deleteUploadedProof.rejected, (state) => {
                state.loading = false;
            })
            .addCase(getCompletedOrderDetails.pending, (state) => {
                state.loading = true;
            })
            .addCase(getCompletedOrderDetails.fulfilled, (state, action) => {
                state.loading = false;
                state.completed_order_details = action.payload;
            })
            .addCase(getCompletedOrderDetails.rejected, (state) => {
                state.loading = false;
            })
            .addCase(getCompletedOrderProducts.pending, (state) => {
                state.loading = true;
            })
            .addCase(getCompletedOrderProducts.fulfilled, (state, action) => {
                state.loading = false;
                state.completed_order_products = action.payload;
            })
            .addCase(getCompletedOrderProducts.rejected, (state) => {
                state.loading = false;
            })
            .addCase(updatePickedQuantity.pending, (state) => {
                state.loading = true;
            })
            .addCase(updatePickedQuantity.fulfilled, (state, action) => {
                state.loading = false;
            })
            .addCase(updatePickedQuantity.rejected, (state) => {
                state.loading = false;
            })
            .addCase(startDropOff.pending, (state) => {
                state.loading = true;
            })
            .addCase(startDropOff.fulfilled, (state) => {
                state.loading = false;
            })
            .addCase(startDropOff.rejected, (state) => {
                state.loading = false;
            })
            .addCase(uploadDeliveryProof.pending, (state) => {
                state.loading = true;
            })
            .addCase(uploadDeliveryProof.fulfilled, (state) => {
                state.loading = false;
            })
            .addCase(uploadDeliveryProof.rejected, (state) => {
                state.loading = false;
            })
            .addCase(uploadActualDeliveryProof.pending, (state) => {
                state.loading = true;
            })
            .addCase(uploadActualDeliveryProof.fulfilled, (state) => {
                state.loading = false;
            })
            .addCase(uploadActualDeliveryProof.rejected, (state) => {
                state.loading = false;
            })
            .addCase(newOrderDetails.pending, (state) => {
                state.loading = true;
            })
            .addCase(newOrderDetails.fulfilled, (state, action) => {
                state.loading = false;
                state.new_order_details = action.payload;
            })
            .addCase(newOrderDetails.rejected, (state) => {
                state.loading = false;
            })
            .addCase(updateDeliveryDP.pending, (state) => { state.loading = true; })
            .addCase(updateDeliveryDP.fulfilled, (state) => { state.loading = false; })
            .addCase(updateDeliveryDP.rejected, (state) => { state.loading = false; })
            .addCase(requestAccountDeletion.pending, (state) => { state.loading = true; })
            .addCase(requestAccountDeletion.fulfilled, (state) => { state.loading = false; })
            .addCase(requestAccountDeletion.rejected, (state) => { state.loading = false; })
            .addCase(getLoginUserData.pending, (state) => {
                state.loading = true;
            })
            .addCase(getLoginUserData.fulfilled, (state, action) => {
                state.loading = false;
                state.profile_data = action.payload;
            })
            .addCase(getLoginUserData.rejected, (state) => {
                state.loading = false;
            })
            .addCase(getEarningStatement.pending, (state) => {
                state.earningLoading = true;
            })
            .addCase(getEarningStatement.fulfilled, (state, action) => {
                state.earningLoading = false;
                state.earningStatement = action.payload;
            })
            .addCase(getEarningStatement.rejected, (state) => {
                state.earningLoading = false;
            })
            .addCase(rejectAssignedDeliveries.pending, (state) => {
                state.reject_loading = true;
            })
            .addCase(rejectAssignedDeliveries.fulfilled, (state) => {
                state.reject_loading = false;
            })
            .addCase(rejectAssignedDeliveries.rejected, (state) => {
                state.reject_loading = false;
            })
            .addCase(getRejectedDeliveryAssignments.pending, (state) => {
                state.rejected_loading = true;
            })
            .addCase(getRejectedDeliveryAssignments.fulfilled, (state, action) => {
                state.rejected_loading = false;
                state.rejected_assignments = action.payload || [];
            })
            .addCase(getRejectedDeliveryAssignments.rejected, (state) => {
                state.rejected_loading = false;
                state.rejected_assignments = [];
            })
            .addCase(reUploadDeliveryProof.pending, (state) => {
                state.reupload_loading = true;
            })
            .addCase(reUploadDeliveryProof.fulfilled, (state) => {
                state.reupload_loading = false;
            })
            .addCase(reUploadDeliveryProof.rejected, (state) => {
                state.reupload_loading = false;
            });

    },
});

export const { resetStatus } = deliverySlice.actions;
export default deliverySlice.reducer;
